"use strict";

import minimongo from "minimongo";

const { MemoryDb, utils } = minimongo;
const { processFind, createUid } = utils;
const sharedDb = new MemoryDb();
const meteorCollections = new Map();

function cloneDoc(doc) {
    return JSON.parse(JSON.stringify(doc));
}

function normalizeOptions(options = {}) {
    const normalized = { ...options };
    if (normalized.limit === "Infinity") delete normalized.limit;
    return normalized;
}

function getNested(obj, path) {
    return path.split(".").reduce((acc, key) => {
        if (acc == null) return undefined;
        return acc[key];
    }, obj);
}

function setNested(obj, path, value) {
    const keys = path.split(".");
    let target = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (target[key] == null || typeof target[key] !== "object") {
            target[key] = {};
        }
        target = target[key];
    }
    target[keys[keys.length - 1]] = value;
}

function unsetNested(obj, path) {
    const keys = path.split(".");
    let target = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (target[key] == null) return;
        target = target[key];
    }
    delete target[keys[keys.length - 1]];
}

function applyModifier(doc, modifier) {
    if (!modifier) return doc;
    if (modifier.$set) {
        Object.keys(modifier.$set).forEach((key) => {
            setNested(doc, key, modifier.$set[key]);
        });
    }
    if (modifier.$unset) {
        Object.keys(modifier.$unset).forEach((key) => {
            unsetNested(doc, key);
        });
    }
    if (modifier.$addToSet) {
        Object.keys(modifier.$addToSet).forEach((key) => {
            const current = getNested(doc, key);
            const list = Array.isArray(current) ? current.slice() : [];
            const value = modifier.$addToSet[key];
            const values = value?.$each ? value.$each : [value];
            values.forEach((item) => {
                if (!list.some((entry) => JSON.stringify(entry) === JSON.stringify(item))) {
                    list.push(item);
                }
            });
            setNested(doc, key, list);
        });
    }
    if (modifier.$push) {
        Object.keys(modifier.$push).forEach((key) => {
            const current = getNested(doc, key);
            const list = Array.isArray(current) ? current.slice() : [];
            const value = modifier.$push[key];
            const values = value?.$each ? value.$each : [value];
            values.forEach((item) => list.push(item));
            setNested(doc, key, list);
        });
    }
    return doc;
}

function getMinimongoCollection(name) {
    if (!sharedDb.collections[name]) sharedDb.addCollection(name);
    return sharedDb.collections[name];
}

class MeteorLocalCursor {
    constructor(store, selector, options) {
        this._store = store;
        this._selector = selector;
        this._options = options;
    }
    _fetchDocs() {
        const items = Object.values(this._store.items);
        return processFind(items, this._selector, this._options);
    }
    fetch() {
        return this._fetchDocs().map((doc) => cloneDoc(doc));
    }
    count() {
        return this._fetchDocs().length;
    }
    forEach(callback) {
        this._fetchDocs().forEach(callback);
    }
}

class MeteorLocalCollection {
    constructor(name) {
        this._name = name;
        this._store = getMinimongoCollection(name);
    }
    insert(document) {
        const doc = cloneDoc(document);
        if (!doc._id) doc._id = createUid();
        this._store.items[doc._id] = doc;
        return doc._id;
    }
    remove(selector) {
        if (typeof selector === "string") {
            if (this._store.items[selector]) {
                delete this._store.items[selector];
                delete this._store.upserts[selector];
                return 1;
            }
            return 0;
        }
        const matches = processFind(Object.values(this._store.items), selector, {});
        matches.forEach((doc) => {
            delete this._store.items[doc._id];
            delete this._store.upserts[doc._id];
        });
        return matches.length;
    }
    find(selector = {}, options = {}) {
        return new MeteorLocalCursor(
            this._store,
            selector,
            normalizeOptions(options)
        );
    }
    findOne(selector = {}, options = {}) {
        const docs = this.find(selector, options).fetch();
        return docs[0];
    }
    upsert(selector, modifier) {
        const matches = processFind(Object.values(this._store.items), selector, {
            limit: 1
        });
        if (matches.length) {
            const stored = this._store.items[matches[0]._id];
            applyModifier(stored, modifier);
            return { numberAffected: 1, insertedId: stored._id };
        }
        const doc = applyModifier(cloneDoc(selector), modifier);
        if (!doc._id) doc._id = createUid();
        this._store.items[doc._id] = doc;
        return { numberAffected: 1, insertedId: doc._id };
    }
}

function Collection(name, options = {}) {
    if (options.connection != null) {
        console.warn(
            `[webpack-parallel] Mongo.Collection("${name}") com connection remota não é suportado; usando minimongo local.`
        );
    }
    if (!meteorCollections.has(name)) {
        meteorCollections.set(name, new MeteorLocalCollection(name));
    }
    return meteorCollections.get(name);
}

const Mongo = { Collection };

export default Mongo;
export { Mongo };
